import React, { useState } from "react";
import {
  Box,
  Card,
  Chip,
  Menu,
  Stack,
  Button,
  MenuItem,
  Typography,
} from "@mui/material";
import {
  Area,
  XAxis,
  AreaChart,
  Tooltip as RechartsTooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import styles from "./riskScoreDetail.module.scss";
import {
  scoreSummary,
  aiSummary,
  recommendedActions,
  riskTrend,
  riskFactors,
} from "./riskScoreData";
import { downloadPDF } from "../hipaa/HipaaDetail";
import ComplianceBreadcrumb from "../compliance/ComplianceBreadcrumb";
import FilterableTable from "../../shared/Tables/FilterableTable";
import { Server } from "lucide-react";

const TrendTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className={styles.tooltipCard}>
      <div className={styles.tooltipTitle}>{label}</div>
      <div className={styles.tooltipValue}>Risk Score: {payload[0].value}</div>
    </div>
  );
};

const RiskScoreDetail = () => {
  const [periodLabel, setPeriodLabel] = useState("Last Year");
  const [anchorEl, setAnchorEl] = useState(null);

  const categories = React.useMemo(
    () => ["all", ...new Set(riskFactors.map((row) => row.category))],
    []
  );
  const statuses = React.useMemo(
    () => ["all", ...new Set(riskFactors.map((row) => row.status))],
    []
  );

  return (
    <Box className={styles.page}>
      <div className={styles.topBar}>
        <ComplianceBreadcrumb
          items={[
            { label: "CISO Dashboard", href: "/" },
            { label: "Risk Score" },
          ]}
        />{" "}
        <Button
          variant="contained"
          startIcon={<DownloadOutlinedIcon />}
          className={styles.exportButton}
          onClick={() => downloadPDF("Risk-score")}
        >
          Export Report
        </Button>
      </div>
      <Stack id="report" gap={2}>
        <Card className={styles.heroCard}>
          <div className={styles.heroLeft}>
            <div className={styles.iconBubble}>
              <ShieldOutlinedIcon />
            </div>
            <div>
              <div className={styles.heroLabel}>Risk Score</div>
              <div className={styles.heroSub}>
                Comprehensive risk assessment based on active vulnerabilities,
                network anomalies, and asset criticality across all OT and IT
                segments.
              </div>
            </div>
          </div>
          <div className={styles.heroRight}>
            <Chip
              label={`${scoreSummary.change} vs 30d`}
              size="small"
              className={`${styles.trendChip} ${
                scoreSummary.change.startsWith("-")
                  ? styles.trendChipDown
                  : styles.trendChipUp
              }`}
              icon={<TrendingUpOutlinedIcon fontSize="small" />}
            />
            <div className={styles.scoreValue}>{scoreSummary.score}/100</div>
          </div>
        </Card>
        <Card className={styles.summaryCard}>
          <div className={styles.summaryHeader}>
            <div className={styles.sectionTitleRow}>
              <InfoOutlinedIcon className={styles.sectionIcon} />
              <Typography className={styles.sectionTitle}>
                AI Generated Summary
              </Typography>
            </div>
            <div className={styles.summaryMeta}>
              Last Updated: {aiSummary.lastUpdated}
            </div>
          </div>

          <Box
            className={styles.summaryBlock}
            sx={{ border: "1px solid rgba(5, 117, 230, 1)" }}
          >
            <div className={styles.blockTitle}>Executive Summary</div>
            <div className={styles.blockBody}>{aiSummary.executive}</div>
          </Box>

          <div className={styles.summaryBlock}>
            <div className={styles.blockTitle}>Recommended Actions</div>
            <div className={styles.actionList}>
              {recommendedActions.map((action) => (
                <Card
                  key={action.title}
                  className={styles.actionCard}
                  sx={{
                    borderLeft: `6px solid ${action.borderColor}`,
                  }}
                >
                  <div className={styles.actionHeader}>
                    <span
                      className={`${styles.actionPriority} ${
                        styles[action.priorityTone]
                      }`}
                    >
                      {action.priority}
                    </span>
                    <span className={styles.actionCve}>{action.cve}</span>
                  </div>
                  <div className={styles.actionTitle}>{action.title}</div>
                  <div className={styles.actionDesc}>{action.description}</div>
                </Card>
              ))}
            </div>
          </div>

          <div className={styles.summaryFootnote}>
            <span>Analysis Confidence: {aiSummary.confidence}</span>
            <span>Methodology: {aiSummary.methodology}</span>
          </div>
        </Card>
        <Card className={styles.trendCard}>
          <div className={styles.trendHeader}>
            <Typography className={styles.sectionTitle} fontWeight={700}>
              Historical Risk Trend
            </Typography>
            <div>
              <Button
                variant="outlined"
                size="small"
                className={styles.filterButton}
                endIcon={<KeyboardArrowDownIcon fontSize="small" />}
                onClick={(e) => setAnchorEl(e.currentTarget)}
              >
                {periodLabel}
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
              >
                {["Last 30 Days", "Last 6 Months", "Last Year"].map((label) => (
                  <MenuItem
                    key={label}
                    selected={label === periodLabel}
                    onClick={() => {
                      setPeriodLabel(label);
                      setAnchorEl(null);
                    }}
                  >
                    {label}
                  </MenuItem>
                ))}
              </Menu>
            </div>
          </div>
          <div className={styles.trendChart}>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart
                data={riskTrend}
                margin={{ top: 10, right: 16, left: 0, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#fbbf24" stopOpacity={0.5} />
                    <stop
                      offset="100%"
                      stopColor="#f59e0b"
                      stopOpacity={0.08}
                    />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
                <XAxis
                  dataKey="month"
                  tick={{ fill: "#cbd5e1", fontSize: 12 }}
                  axisLine={{ stroke: "#1e293b" }}
                  tickLine={{ stroke: "#1e293b" }}
                />
                <RechartsTooltip
                  content={<TrendTooltip />}
                  cursor={{ stroke: "#fbbf24", strokeDasharray: "4 4" }}
                />
                <Area
                  type="monotone"
                  dataKey="score"
                  stroke="#f59e0b"
                  strokeWidth={3}
                  fillOpacity={1}
                  fill="url(#trendFill)"
                  dot={{
                    r: 5,
                    fill: "#fbbf24",
                    stroke: "#0f172a",
                    strokeWidth: 1.5,
                  }}
                  activeDot={{ r: 6, fill: "#f59e0b" }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <FilterableTable
          title="Risk Factors"
          data={riskFactors}
          columns={[
            {
              id: "asset",
              label: "Asset Name",
              headClassName: styles.tableHead,
              renderCell: (row) => (
                <div className={styles.assetCell}>
                  <Server />
                  <div>
                    <div className={styles.assetName}>{row.asset}</div>
                  </div>
                </div>
              ),
            },
            {
              id: "category",
              label: "Category",
              headClassName: styles.tableHead,
            },
            {
              id: "score",
              label: "Risk Score",
              headClassName: styles.tableHead,
              renderCell: (row) => (
                <div className={styles.riskScoreRow}>
                  <div className={styles.riskScoreLabel}>{row.score}</div>
                  <div className={styles.riskScoreMeter}>
                    <span
                      className={styles.riskScoreFill}
                      style={{ width: `${row.score}%` }}
                    />
                  </div>
                </div>
              ),
            },
            {
              id: "cve",
              label: "CVE/Vulnerability",
              headClassName: styles.tableHead,
            },
            {
              id: "lastScanned",
              label: "Last Scanned",
              headClassName: styles.tableHead,
            },
            {
              id: "status",
              label: "Status",
              headClassName: styles.tableHead,
              renderCell: (row) => (
                <span className={styles.statusProtected}>{row.status}</span>
              ),
            },
          ]}
          showFilterToggle
          searchKeys={["asset"]}
          searchPlaceholder="Search by Asset Name"
          filters={[
            {
              id: "riskBucket",
              label: "Risk",
              options: ["all", "high", "medium", "low"],
              filterFn: (row, selected) => {
                if (selected === "high") return row.score >= 80;
                if (selected === "medium")
                  return row.score >= 60 && row.score < 80;
                if (selected === "low") return row.score < 60;
                return true;
              },
              formatOption: (option) => {
                if (option === "all") return "All";
                if (option === "high") return "High (80+)";
                if (option === "medium") return "Medium (60-79)";
                return "Low (<60)";
              },
            },
            {
              id: "category",
              label: "Category",
              options: categories,
              valueAccessor: (row) => row.category,
              formatOption: (option) =>
                option === "all" ? "All Categories" : option,
            },
            {
              id: "status",
              label: "Status",
              options: statuses,
              valueAccessor: (row) => row.status,
              formatOption: (option) =>
                option === "all" ? "All Statuses" : option,
            },
          ]}
          emptyMessage="No risk factors match the selected filters."
          rowsPerPageOptions={[5, 10, 25]}
          classNames={{
            card: styles.tableCard,
            tableHeader: styles.tableHeader,
            filterToggle: styles.filterToggle,
            tableHeaderActions: styles.tableHeaderActions,
            sectionTitle: styles.sectionTitle,
            searchField: styles.searchField,
            filterRow: styles.filterRow,
            filterGroup: styles.filterGroup,
            filterLabel: styles.filterLabel,
            filterSelect: styles.filterSelect,
            clearFilters: styles.clearFilters,
            tableContainer: styles.tableContainer,
            tableHead: styles.tableHead,
            tablePagination: styles.tablePagination,
            emptyState: styles.emptyState,
          }}
        />{" "}
      </Stack>
    </Box>
  );
};

export default RiskScoreDetail;
